import { CommonModule } from '@angular/common';
import { Component, ElementRef, OnInit, ViewChild, OnDestroy } from '@angular/core';
import { forkJoin, Observable, of, Subscription, Subject } from 'rxjs';
import { takeUntil, catchError } from 'rxjs/operators';
import { DomSanitizer, SafeHtml } from '@angular/platform-browser';
import { ActivatedRoute, Router } from '@angular/router';
import { ApiCallsService } from '../../../shared/services/api-calls.service';
import { NgbTooltip } from '@ng-bootstrap/ng-bootstrap';
import { FormsModule } from '@angular/forms';

export interface MeterReading {
  locationNo: string;
  meterSerialNo: string;
  readingReceived: string;
  dicName: string;
  dicReadingReceived: string;
  dl: string;
  balanceAmount: number;
  meterStatus: 'Y' | 'N';
  dicStatus: 'Y' | 'N';
  state: any
  lastPowerCutTime: any;
  _raw: Record<string, any>; // 👈 add this

}
export interface ChargingStatusItem {
  locationNo: string;
  meterSerialNo: string;
  state: 'Restored' | 'Cut';           // from API: "state"
  lastPowerCutTime: string;            // from API: "last_power_cut_time" e.g. "2023-02-12 14:11:29"
  isCharging: boolean;                 // true when state === 'Restore'
  durationLabel: string;               // e.g. "3d 4h 22m" — how long charging (since last_power_cut_time)
  mobile_no: string
}

export interface Site {
  srNo: number;
  siteCode: string;
  name: string;
  supervisorName: string;
  supervisorContact: string;
  meters: MeterReading[];
}

export interface SiteSummaryStats {
  totalSites: number;
  totalMeters: number;
  normalCount: number;
  staleMeterCount: number;
  staleDicCount: number;
  Meters1dplus: number
}

export type FilterMode = 'all' | 'normal' | 'meter_flagged' | 'dic_flagged';

export interface FlatMeterRow {
  site: Site;
  meter: MeterReading;
  isFirstInSite: boolean;
  siteRowspan: number;
  siteSyncStatus: 'ok' | 'lagging';
  siteLaggingCount: number;
  meterReadingRecieved: string;
  dicReeadingRecieved: string;
}

@Component({
  selector: 'app-ev-summary',
  imports: [CommonModule, NgbTooltip, FormsModule],
  templateUrl: './ev-summary.component.html',
  styleUrl: './ev-summary.component.scss'
})
export class EvSummaryComponent implements OnInit, OnDestroy {
  stats: SiteSummaryStats | null = null;
  sites: Site[] = [];
  flatRows: FlatMeterRow[] = [];
  activeFilter: FilterMode = 'all';
  showFilterBar = false;
  activeChargeTab: string | 'all' | 'charging' | 'cut' | 'long-charging' = 'all';
  chargeSearchQuery = '';
  filteredChargingList: any[] = [];
  // Add to component class

  readonly FIXED_HEADERS = [
    { key: 'site_code', name: 'Site Code', show: true, _fixed: true, _rowspan: true },
    { key: 'site_name', name: 'Site Name', show: true, _fixed: true, _rowspan: true },
  ];

  get allTableHeaders(): any[] {
    return [...this.FIXED_HEADERS, ...this.tableHeaders];
  }
  // Pagination
  currentPage = 1;
  pageSize = 10;
  readonly pageSizeOptions = [10, 20, 50, 100];

  get totalPages(): number {
    return Math.ceil(this.filteredRows.length / this.pageSize) || 1;
  }

  get paginatedRows(): FlatMeterRow[] {
    const start = (this.currentPage - 1) * this.pageSize;
    const slice = this.filteredRows.slice(start, start + this.pageSize);

    // Recompute isFirstInSite and siteRowspan relative to this page slice
    const sitePageMap = new Map<number, FlatMeterRow[]>();
    for (const r of slice) {
      if (!sitePageMap.has(r.site.srNo)) sitePageMap.set(r.site.srNo, []);
      sitePageMap.get(r.site.srNo)!.push(r);
    }

    return slice.map(r => {
      const group = sitePageMap.get(r.site.srNo)!;
      return {
        ...r,
        isFirstInSite: group[0] === r,
        siteRowspan: group.length
      };
    });
  }

  get pageNumbers(): number[] {
    const total = this.totalPages;
    const current = this.currentPage;

    if (total <= 7) return Array.from({ length: total }, (_, i) => i + 1);

    const pages: number[] = [1];
    if (current > 3) pages.push(-1); // ellipsis

    const start = Math.max(2, current - 1);
    const end = Math.min(total - 1, current + 1);
    for (let i = start; i <= end; i++) pages.push(i);

    if (current < total - 2) pages.push(-1); // ellipsis
    pages.push(total);
    return pages;
  }

  goToPage(page: number): void {
    if (page < 1 || page > this.totalPages) return;
    this.currentPage = page;
  }

  setPageSize(size: any): void {
    this.pageSize = Number(size);
    this.currentPage = 1;
  }
  getCellTemplate(key: string): string {
    switch (key) {
      case 'site_code': return 'site_code';
      case 'site_name': return 'site_name';
      case 'balance_amt': return 'balance';
      case 'state': return 'state';
      case 'meter_reading_received': return 'meter_reading';
      case 'dic_reading_received': return 'dic_reading';
      default: return key;
    }
  }
  statCards: {
    id: number;
    title: string;
    total: number;
    Positive: number;
    Positive_Name: string;
    Negative: number;
    Negative_Name: string;
    show: boolean;
    key: string;
    url?: string;
    class1: string;
    class2: string;
    imagepath: string;
    leftimagepath?: string;
    rightimagepath?: string;
  }[] = [];
  siteid: string = ''
  tableHeaders: any[] = [];
  searchTerm: string = '';

  constructor(
    private sanitizer: DomSanitizer,
    private route: ActivatedRoute,
    private router: Router,
    private apiservice: ApiCallsService
  ) { }

  onCardClick(item: any): void {
    const rawData = item._raw || item;
    try {
      localStorage.setItem('EV_DATA', JSON.stringify(rawData));
      sessionStorage.setItem('EV_DATA', JSON.stringify(rawData));
    } catch (e) {
      console.error('Error storing EV_DATA', e);
    }
    const locId = item._raw?.['location_id'] || item.location_id || item.locationNo || '';
    const siteId = item._raw?.['site_id'] || item.raw_site_id || this.siteid || '';

    if (siteId) {
      localStorage.setItem('selected_site_id', String(siteId));
      sessionStorage.setItem('consumerSiteId', String(siteId));
    }

    const combinedUser = siteId ? `${locId}__${siteId}` : String(locId);

    this.router.navigate(['/ev_charger'], {
      queryParams: {
        user: combinedUser ? btoa(combinedUser) : ''
      }
    });
  }

  onRowClick(row: FlatMeterRow): void {
    const rawData = row.meter._raw || row.meter;
    try {
      localStorage.setItem('EV_DATA', JSON.stringify(rawData));
      sessionStorage.setItem('EV_DATA', JSON.stringify(rawData));
    } catch (e) {
      console.error('Error storing EV_DATA', e);
    }
    const locId = row.meter._raw?.['location_id'] || row.meter.locationNo || '';
    const siteId = row.meter._raw?.['site_id'] || this.siteid || '';

    if (siteId) {
      localStorage.setItem('selected_site_id', String(siteId));
      sessionStorage.setItem('consumerSiteId', String(siteId));
    }

    const combinedUser = siteId ? `${locId}__${siteId}` : String(locId);

    this.router.navigate(['/ev_charger'], {
      queryParams: {
        user: combinedUser ? btoa(combinedUser) : ''
      }
    });
  }

  private destroy$ = new Subject<void>();

  ngOnInit(): void {
    this.route.queryParamMap.pipe(takeUntil(this.destroy$)).subscribe(params => {
      const encodedId = params.get('siteid') || localStorage.getItem('site_idLogin');
      this.siteid = encodedId ? atob(encodedId) : '';
      console.log(this.siteid);
      this.getEVdetails(); // 👈 call API here

    });
    this.flatRows = this.buildFlatRows();
    this.getStats().pipe(takeUntil(this.destroy$)).subscribe(s => {
      this.stats = s;
      if (this.siteid && this.siteid !== 'ALL') {
        this.buildStatCards(true);
      }
      else {
        this.buildStatCards(false);
      }
    });
  }
  
  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
    if (this.evsubs) {
      this.evsubs.unsubscribe();
    }
    if (this.mergeSub) {
      this.mergeSub.unsubscribe();
    }
  }

  evsubs!: Subscription;
  mergeSub!: Subscription;
  sitelist: string = '';
  locationList: string[] = []
  getEVdetails() {
    const payload = { site_id: this.siteid };

    if (this.evsubs) {
      this.evsubs.unsubscribe();
    }

    this.evsubs = this.apiservice.EVDetails(payload).pipe(takeUntil(this.destroy$)).subscribe(res => {

      const tableData = res.Data;
      // const tableData = testdata.Data
      const list = tableData.list;
      const headers = tableData.header;
      this.sitelist = [
        ...new Set(
          list.map((element: any) => element.site_id)
        )
      ].join(',');    // ✅ store headers for dynamic table
      this.locationList = list.map((element: any) => element.location_id)
      const ignoreKeys = [
        'site_name',
        'short_code',
        'supervisor_name',
        'supervisor_contact_no'
      ];

      this.tableHeaders = headers.filter((h: any) =>
        h.show && !ignoreKeys.includes(h.key)
      );
      // ✅ transform API → Site[]
      this.sites = this.transformApiToSites(list);

      // rebuild everything
      this.flatRows = this.buildFlatRows();
      this.chargingStatusList = this.buildChargingStatus();
      this.mergeAllData(); // ✅ only here, after sitelist is ready

      this.applyChargeFilters();
      this.applyTableFilters()

      this.getStats().pipe(takeUntil(this.destroy$)).subscribe(s => {
        this.stats = s;

        if (this.siteid && this.siteid !== 'ALL') {
          this.buildStatCards(true);
        }
        else {
          this.buildStatCards(false);
        }
      });
    });
  }

  // Fix property declarations at the top of the class
  tableHeadersSiteList: any[] = []; // ✅ Initialize as empty array
  tableHeadersLocationList: any[] = []; // ✅ Change to array and initialize
  mergedData: any[] = []; // ✅ Initialize as empty array
  originalSiteListData: any[] = [];
  siteSearchTerm: string = '';

  applySiteListFilter(): void {
    if (!this.siteSearchTerm) {
      this.mergedData = [...this.originalSiteListData];
    } else {
      const q = this.siteSearchTerm.toLowerCase();
      this.mergedData = this.originalSiteListData.filter(row => {
        return this.displayHeaders.some(h => {
          const val = this.getValue(row, h.key);
          return String(val).toLowerCase().includes(q);
        });
      });
    }
    this.siteListCurrentPage = 1;
  }

  exportSiteListCSV(): void {
    if (this.displayHeaders.length === 0 || this.mergedData.length === 0) return;
    const headerRow = this.displayHeaders.map(h => {
      return h.key === 'mobile_no' ? 'User Mobile No' : h.name;
    }).join(',');

    const csvRows = this.mergedData.map(row => {
      return this.displayHeaders.map(h => {
        const val = this.getValue(row, h.key);
        const str = String(val ?? '');
        return str.includes(',') || str.includes('"')
          ? `"${str.replace(/"/g, '""')}"`
          : str;
      }).join(',');
    });

    const csv = [headerRow, ...csvRows].join('\n');
    const blob = new Blob([csv], { type: 'text/csv' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `site_location_details_${new Date().toISOString().slice(0, 10)}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  }

  // Fix the getter to handle empty mergedData
  get siteListTotalPages(): number {
    if (!this.mergedData || this.mergedData.length === 0) return 1; // ✅ Add null check
    return Math.ceil(this.mergedData.length / this.siteListPageSize) || 1;
  }

  get paginatedSiteListRows(): any[] {
    if (!this.mergedData || this.mergedData.length === 0) return []; // ✅ Add null check
    const start = (this.siteListCurrentPage - 1) * this.siteListPageSize;
    return this.mergedData.slice(start, start + this.siteListPageSize);
  }

  getSiteListRangeEnd(): number {
    if (!this.mergedData) return 0; // ✅ Add null check
    return Math.min(this.siteListCurrentPage * this.siteListPageSize, this.mergedData.length);
  }
  isSiteListOpen = false;
  siteListCurrentPage = 1;
  siteListPageSize = 10;
  readonly siteListPageSizeOptions = [10, 20, 50, 100];
  displayHeaders: any[] = [];

  // Fix mergeAllData to handle the locationList check properly
  mergeAllData() {
    const siteIds = this.sitelist.split(',').filter(id => id.trim());

    if (siteIds.length === 0) return;

    const locationApiCalls = siteIds.map(siteId => {
      const payload = { site_id: siteId.trim(), type: 'FLAT', Limit: 10000, Offset: 0 };
      return this.apiservice.TowerDetailsTableData('Location-Configuration', payload).pipe(
        catchError(err => {
          console.error(`Error fetching Location-Configuration for site ${siteId}`, err);
          return of({ Data: { list: [] } });
        })
      );
    });

    if (this.mergeSub) {
      this.mergeSub.unsubscribe();
    }

    this.mergeSub = forkJoin({
      siteData: this.apiservice.getSiteList(this.sitelist).pipe(
        catchError(err => {
          console.error('Error fetching site list', err);
          return of({ Data: { list: [], header: [] } });
        })
      ),
      locationDataArray: forkJoin(locationApiCalls)
    }).pipe(
      takeUntil(this.destroy$)
    ).subscribe(({ siteData, locationDataArray }) => {

      // ── 1. Site list table (simple, no merge) ──────────────────────
      const siteList = siteData.Data.list;
      const siteHeaders = siteData.Data.header || [];

      this.originalSiteListData = [...siteList];
      this.mergedData = siteList; // just site list, no location merge
      this.mergeSiteListHeaders(siteHeaders, []);
      this.processHeaders();

      // ── 2. Build location lookup: id → mobile_no ───────────────────
      const locationList = locationDataArray.reduce((acc: any[], response: any) => {
        return acc.concat(response.Data.list || []);
      }, []);

      const locationMobileMap = new Map<string, string>(
        locationList.map((loc: any) => [String(loc.id), loc.mobile_no ?? '—'])
      );

      // ── 3. Inject mobile_no into each meter's _raw + enrich sites ──
      this.sites = this.sites.map(site => ({
        ...site,
        meters: site.meters.map(meter => ({
          ...meter,
          _raw: {
            ...meter._raw,
            mobile_no: locationMobileMap.get(String(meter._raw?.['location_id'])) ?? '—'
          }
        }))
      }));

      // ── 4. Add mobile_no header to EV table if not already present ─
      const alreadyHasMobile = this.tableHeaders.some(h => h.key === 'mobile_no');
      if (!alreadyHasMobile) {
        this.tableHeaders = [
          ...this.tableHeaders,
          { key: 'mobile_no', name: 'User Mobile No', show: true }
        ];
      }

      // Rebuild flat rows to reflect the enriched meter data
      this.flatRows = this.buildFlatRows();
      this.applyTableFilters();
      this.chargingStatusList = this.buildChargingStatus();
      this.applyChargeFilters();


      console.log('Site List:', this.mergedData);
      console.log('Table Headers (with mobile):', this.tableHeaders);
    });
  }
  mergeSiteListHeaders(siteHeaders: any[], locationHeaders: any[]) {
    const headerMap = new Map<string, any>();

    siteHeaders.forEach((header: any) => {
      if (header.key) {
        headerMap.set(header.key, header);
      }
    });

    locationHeaders.forEach((header: any) => {
      if (header.key && !headerMap.has(header.key)) {
        headerMap.set(header.key, header);
      }
    });

    // Store in a different property - don't override tableHeaders
    this.tableHeadersSiteList = Array.from(headerMap.values());
  }
  headershowlist = [
    'site_name',
    'location_id',
    'meter_serial_no',
    'dic_name',
    'reading_received',
    'status',
    'mobile_no',
    'supervisor_contact_no'
    // Add more keys that should always display
  ];
  headerblacklist = [
    'tower_id',
    'load_type'
    // Add more keys that should always display
  ];


  get siteListPageNumbers(): number[] {
    const total = this.siteListTotalPages;
    const current = this.siteListCurrentPage;

    if (total <= 7) return Array.from({ length: total }, (_, i) => i + 1);

    const pages: number[] = [1];
    if (current > 3) pages.push(-1);

    const start = Math.max(2, current - 1);
    const end = Math.min(total - 1, current + 1);
    for (let i = start; i <= end; i++) pages.push(i);

    if (current < total - 2) pages.push(-1);
    pages.push(total);
    return pages;
  }

  goToSiteListPage(page: number): void {
    if (page < 1 || page > this.siteListTotalPages) return;
    this.siteListCurrentPage = page;
  }

  setSiteListPageSize(size: any): void {
    this.siteListPageSize = Number(size);
    this.siteListCurrentPage = 1;
  }

  toggleSiteListAccordion(): void {
    this.isSiteListOpen = !this.isSiteListOpen;
  }



  // Get value from merged data using header key
  getValue(row: any, key: string): any {
    return row[key] !== undefined && row[key] !== null && row[key] !== '' ? row[key] : '—';
  }
  // Fix processHeaders to check if tableHeadersSiteList exists
  processHeaders() {
    if (!this.tableHeadersSiteList || this.tableHeadersSiteList.length === 0) {
      this.displayHeaders = [];
      return;
    }

    this.displayHeaders = this.tableHeadersSiteList.filter((header: any) => {
      if (this.headershowlist.includes(header.key)) {
        return true;
      }
      if (this.headerblacklist.includes(header.key)) {
        return false;
      }
      return header.show !== false && header.display !== false;
    });
  }

  getCellValue(row: FlatMeterRow, key: string): any {
    // Site-level keys
    switch (key) {
      case 'site_code': return row.site.siteCode;
      case 'site_name': return row.site.name;
    }
    // Everything else — look up directly from raw API item by key
    const val = row.meter._raw?.[key];
    return val !== undefined && val !== null && val !== '' ? val : '—';
  }
  isOpen = false;


  getSiteLookupMap(): Map<string, any> {
    const siteListStr = localStorage.getItem('siteList');
    const siteList = siteListStr ? JSON.parse(siteListStr) : [];

    const map = new Map<string, any>();

    siteList.forEach((site: any) => {
      map.set(site.site_id, site);
    });

    return map;
  }

  transformApiToSites(list: any[]): Site[] {
    const siteMap = new Map<string, Site>();
    const siteLookup = this.getSiteLookupMap(); // 👈 from localStorage
    let srCounter = 1;

    list.forEach(item => {
      const siteId = item.site_id;

      // 👇 get site info from localStorage
      const siteInfo = siteLookup.get(siteId) || {};

      if (!siteMap.has(siteId)) {
        siteMap.set(siteId, {
          srNo: srCounter++,

          // ✅ priority: localStorage → fallback API → fallback dash
          siteCode: siteInfo.short_code || item.short_code || '—',
          name: siteInfo.site_name || item.site_name || '—',
          supervisorName: siteInfo.supervisor_name || item.supervisor_name || '—',
          supervisorContact: siteInfo.supervisor_contact_no || item.supervisor_contact_no || '',

          meters: []
        });
      }

      const site = siteMap.get(siteId)!;

      const meter: MeterReading = {
        locationNo: item.location_no,
        meterSerialNo: item.meter_sr_no,
        readingReceived: item.meter_reading_received,
        dicName: item.dic_name,
        dicReadingReceived: item.dic_reading_received,
        dl: item.data_logger_id,
        balanceAmount: Number(item.balance_amt),

        meterStatus: item.meter_status === 'Y' ? 'Y' : 'N',
        dicStatus: item.dic_status === 'Y' ? 'Y' : 'N',

        state: item.state,
        lastPowerCutTime: item.last_power_cut_time,
        _raw: item  // 👈 add this


      };

      site.meters.push(meter);
    });

    return Array.from(siteMap.values());
  }
  // ── Flat rows builder ─────────────────────────────────────────────

  buildFlatRows(): FlatMeterRow[] {
    const rows: FlatMeterRow[] = [];
    for (const site of this.sites) {
      const laggingCount = site.meters.filter(
        m => m.meterStatus === 'Y' || m.dicStatus === 'Y'
      ).length;
      const siteSyncStatus: 'ok' | 'lagging' = laggingCount === 0 ? 'ok' : 'lagging';

      site.meters.forEach((meter, i) => {
        rows.push({
          site,
          meter,
          isFirstInSite: i === 0,
          siteRowspan: site.meters.length,
          siteSyncStatus,
          siteLaggingCount: laggingCount,
          meterReadingRecieved: meter.readingReceived,
          dicReeadingRecieved: meter.dicReadingReceived,
        });
      });
    }
    return rows;
  }

  // ── Filtered rows ─────────────────────────────────────────────────

  filteredRows: FlatMeterRow[] = [];


  /** Alert strip: any meter where meterStatus or dicStatus is 'Y' */
  get stalertRows(): FlatMeterRow[] {
    return this.flatRows.filter(r => r.meter.meterStatus === 'Y' || r.meter.dicStatus === 'Y');
  }
  applyTableFilters(): void {
    let filtered: FlatMeterRow[];

    switch (this.activeFilter) {
      case 'normal':
        filtered = this.flatRows.filter(r => r.meter.meterStatus === 'N' && r.meter.dicStatus === 'N');
        break;
      case 'meter_flagged':
        filtered = this.flatRows.filter(r => r.meter.meterStatus === 'Y');
        break;
      case 'dic_flagged':
        filtered = this.flatRows.filter(r => r.meter.dicStatus === 'Y');
        break;
      default:
        filtered = [...this.flatRows];
    }

    const q = this.searchTerm?.trim().toLowerCase();
    if (q) {
      filtered = filtered.filter(r =>
        r.site.siteCode?.toLowerCase().includes(q) ||
        r.site.name?.toLowerCase().includes(q) ||
        r.site.supervisorName?.toLowerCase().includes(q) ||
        r.meter.locationNo?.toLowerCase().includes(q) ||
        r.meter.meterSerialNo?.toLowerCase().includes(q) ||
        r.meter.dicName?.toLowerCase().includes(q)
      );
    }

    // Recompute rowspan
    const siteMap = new Map<number, FlatMeterRow[]>();
    for (const r of filtered) {
      if (!siteMap.has(r.site.srNo)) siteMap.set(r.site.srNo, []);
      siteMap.get(r.site.srNo)!.push(r);
    }

    this.filteredRows = filtered.map(r => {
      const group = siteMap.get(r.site.srNo)!;
      return {
        ...r,
        isFirstInSite: group[0] === r,
        siteRowspan: group.length
      };
    });
    this.currentPage = 1; // ← add this at the end

  }
  getRangeEnd(): number {
    return Math.min(this.currentPage * this.pageSize, this.filteredRows.length);
  }
  // Update setFilter to call it
  setFilter(f: FilterMode): void {
    this.activeFilter = f;
    this.applyTableFilters();
  }
  reset() {
    this.searchTerm = ''
    this.setFilter('all')
  }
  // Update toggleFilterBar
  toggleFilterBar(): void {
    this.showFilterBar = !this.showFilterBar;
  }
  // ── Status badge helpers ─────────────────────────────────────────

  getMeterStatusClass(s: 'Y' | 'N'): string {
    return s === 'Y' ? 'status-badge status-flagged' : 'status-badge status-normal';
  }
  getMeterStatusLabel(s: 'Y' | 'N'): string { return s === 'Y' ? 'Stale' : 'Normal'; }

  getDicStatusClass(s: 'Y' | 'N'): string {
    return s === 'Y' ? 'status-badge status-flagged' : 'status-badge status-normal';
  }
  getDicStatusLabel(s: 'Y' | 'N'): string { return s === 'Y' ? 'Stale' : 'Normal'; }

  // ── Balance helpers ───────────────────────────────────────────────

  getBalanceClass(amount: number): string {
    if (amount < 0) return 'balance-neg';
    if (amount > 5000) return 'balance-pos';
    if (amount < 100) return 'balance-med';
    return 'balance-pos';
  }

  // ── Export ───────────────────────────────────────────────────────

  exportCSV(): void {
    const exportHeaders = [
      { key: 'site_code', name: 'Site Code' },
      { key: 'site_name', name: 'Site Name' },
      { key: 'supervisor_name', name: 'Supervisor Name' },
      { key: 'supervisor_contact', name: 'Supervisor Contact' },
      ...this.tableHeaders
    ];
    const headerRow = exportHeaders.map(h => h.name).join(',');

    const csvRows = this.filteredRows.map(r =>
      exportHeaders.map(h => {
        let val: any;
        if (h.key === 'site_code') val = r.site.siteCode;
        else if (h.key === 'site_name') val = r.site.name;
        else if (h.key === 'supervisor_name') val = r.site.supervisorName;
        else if (h.key === 'supervisor_contact') val = r.site.supervisorContact;
        else val = this.getCellValue(r, h.key);

        const str = String(val ?? '');
        // Wrap in quotes if contains comma or quote
        return str.includes(',') || str.includes('"')
          ? `"${str.replace(/"/g, '""')}"`
          : str;
      }).join(',')
    );

    const csv = [headerRow, ...csvRows].join('\n');
    const blob = new Blob([csv], { type: 'text/csv' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `ev_meters_${this.activeFilter}_${new Date().toISOString().slice(0, 10)}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  }

  // ── Stats ─────────────────────────────────────────────────────────

  getStats(): Observable<SiteSummaryStats> {
    const all = this.sites.flatMap(s => s.meters);
    return of({
      totalSites: this.sites.length,
      totalMeters: all.length,
      normalCount: all.filter(m => m.meterStatus === 'N' && m.dicStatus === 'N').length,
      staleMeterCount: all.filter(m => m.meterStatus === 'Y').length,
      staleDicCount: all.filter(m => m.dicStatus === 'Y').length,
      Meters1dplus: all.filter(m =>
        this.getMinutesSince(m.lastPowerCutTime) > 1440 // 1 day = 1440 mins
      ).length,
    });
  }
  activeTab: string = ''

  get meterStaleRows() {
    return this.stalertRows.filter(r => r.meter.meterStatus === 'Y');
  }

  get dicStaleRows() {
    return this.stalertRows.filter(r => r.meter.dicStatus === 'Y');
  }

  switchTab(tab: string = 'meter'): void {
    this.activeTab = tab;
    if (!this.isOpen && (tab === 'meter' || tab === 'dic')) {
      this.isOpen = true;

      setTimeout(() => {
        const yOffset = -80; // adjust this value
        const element = this.AlertList.nativeElement;

        const y =
          element.getBoundingClientRect().top +
          window.pageYOffset +
          yOffset;

        window.scrollTo({
          top: y,
          behavior: 'smooth',
        });
      });
    }
    if (!this.isSiteListOpen && tab === 'sitelist') {
      this.isSiteListOpen = true;

      setTimeout(() => {
        const yOffset = -80;
        const element = this.SiteList.nativeElement;

        const y =
          element.getBoundingClientRect().top +
          window.pageYOffset +
          yOffset;

        window.scrollTo({
          top: y,
          behavior: 'smooth',
        });
      });
    }
    if (tab === 'totalEV') {
      setTimeout(() => {
        const yOffset = -80;
        const element = this.target.nativeElement;

        const y =
          element.getBoundingClientRect().top +
          window.pageYOffset +
          yOffset;

        window.scrollTo({
          top: y,
          behavior: 'smooth',
        });
      });
    }


  }

  toggleAccordion(): void {
    this.isOpen = !this.isOpen;
    if (this.isOpen) {
      this.switchTab('meter')
    }
  }
  buildStatCards(siteid: boolean = false): void {
    if (!this.stats) return;
    this.statCards = [
      {
        id: 2,
        title: 'Total EV',
        total: this.stats.totalMeters,
        Positive: 0,
        Positive_Name: '',
        Negative: 0,
        Negative_Name: '',
        show: false,
        key: 'totalEV',
        class1: 'total-Complete border-b-sensorcolor',
        class2: 'sensorcolor',
        imagepath: 'assets/DashboardIcon/sensor.png'
      },
      {
        id: 4,
        title: 'Charging duration > 24hrs ',
        total: this.longchargingMeters.length,
        Positive: 0,
        Positive_Name: '',
        Negative: 0,
        Negative_Name: '',
        show: true,
        key: 'long-charging',
        class1: 'total-Complete border-b-wqqtycolor',
        class2: 'wqqtycolor',
        imagepath: 'assets/DashboardIcon/sensor.png'
      },
      {
        id: 5,
        title: 'Stale Meter Reading',
        total: this.stats.staleMeterCount,
        Positive: 0,
        Positive_Name: 'Meter last reading time - 3 min ago',
        Negative: 0,
        Negative_Name: '',
        show: false,
        key: 'meter',
        class1: 'total-Progress border-b-dlcolor',
        class2: 'dlcolor',
        imagepath: 'assets/DashboardIcon/DL.png'
      },
      {
        id: 6,
        title: 'Stale CB Reading',
        total: this.stats.staleDicCount,
        Positive: 0,
        Positive_Name: 'CB Last reading time - 30 min ago',
        Negative: 0,
        Negative_Name: '',
        show: false,
        key: 'dic',
        class1: 'total-Complete border-b-wqqtycolor',
        class2: 'wqqtycolor',
        imagepath: 'assets/DashboardIcon/sensor.png'
      }
    ];
    if (!siteid) {
      this.statCards = [{
        id: 1,
        title: 'Total Sites',
        total: this.stats.totalSites,
        Positive: 0,
        Positive_Name: '',
        Negative: 0,
        Negative_Name: '',
        show: false,
        key: 'sitelist',
        class1: 'total-project border-b-siteBorderColor',
        class2: 'siteBorderColor',
        imagepath: 'assets/DashboardIcon/site.png'
      }, ...this.statCards]
    }
  }

  // ── Raw data ─────────────────────────────────────────────────────
  // Map API fields: meter_status → meterStatus, dic_status → dicStatus


  // ─────────────────────────────────────────────────────────────────────────────
  // ADD these to your existing component — interfaces, properties, and methods
  // ─────────────────────────────────────────────────────────────────────────────

  // ── Interface (add alongside your existing interfaces) ────────────────────────

  @ViewChild('targetSection') target!: ElementRef;
  @ViewChild('ChargingSection') chargetarget!: ElementRef;
  @ViewChild('AlertList') AlertList!: ElementRef;
  @ViewChild('SiteList') SiteList!: ElementRef;


  // ── Properties (add to your component class) ──────────────────────────────────
  filteroncardclick(unit_no: string = '') {
    this.showFilterBar = true
    this.setFilter('all')
    this.searchTerm = unit_no
    this.applyTableFilters()
    this.target.nativeElement.scrollIntoView({
      behavior: 'smooth',
      block: 'start'
    });

  }
  chargingStatusList: ChargingStatusItem[] = [];

  get chargingMeters(): ChargingStatusItem[] {
    return this.chargingStatusList.filter(i => i.isCharging);
  }

  get cutMeters(): ChargingStatusItem[] {
    return this.chargingStatusList.filter(i => !i.isCharging);
  }

  get longchargingMeters(): ChargingStatusItem[] {
    return this.chargingStatusList.filter(i => {
      if (!i.isCharging || !i.lastPowerCutTime) return false;

      return this.getMinutesSince(i.lastPowerCutTime) > 1440;
    });
  }
  switchChargeTab(tab: string | 'all' | 'charging' | 'cut' | 'long-charging', scroll: boolean = false): void {
    this.activeChargeTab = tab;
    this.applyChargeFilters();
    if (scroll) {
      setTimeout(() => {
        const yOffset = -80;
        const element = this.chargetarget.nativeElement;

        const y =
          element.getBoundingClientRect().top +
          window.pageYOffset +
          yOffset;

        window.scrollTo({
          top: y,
          behavior: 'smooth',
        });
      });
    }
  }

  onChargeSearch(): void {
    this.applyChargeFilters();
  }

  clearChargeSearch(): void {
    this.chargeSearchQuery = '';
    this.applyChargeFilters();
  }

  applyChargeFilters(): void {
    let list = [...this.chargingStatusList];

    // Tab filter
    if (this.activeChargeTab === 'charging') {
      list = list.filter(i => i.isCharging);
    }

    if (this.activeChargeTab === 'cut') {
      list = list.filter(i => !i.isCharging);
    }

    // 🔥 NEW: charging > 1 day filter
    if (this.activeChargeTab === 'long-charging') {
      list = list.filter(i => {
        if (!i.isCharging || !i.lastPowerCutTime) return false;

        return this.getMinutesSince(i.lastPowerCutTime) > 1440;
      });
    }

    // Search filter — matches locationNo or meterSerialNo
    const q = this.chargeSearchQuery.trim().toLowerCase();
    if (q) {
      list = list.filter(i =>
        i.locationNo?.toLowerCase().includes(q) ||
        i.meterSerialNo?.toLowerCase().includes(q)
      );
    }

    this.filteredChargingList = list;
  }
  // ── Call this in ngOnInit() after sites are loaded ────────────────────────────
  // this.chargingStatusList = this.buildChargingStatus();

  // ── Method (add to your component class) ─────────────────────────────────────

  buildChargingStatus(): ChargingStatusItem[] {
    // Flatten all meters from all sites into charging status items.
    // Map API fields: state → state, last_power_cut_time → lastPowerCutTime
    return this.sites.flatMap(site =>
      site.meters.map(meter => {
        const isCharging = (meter as any).state === 'Restored';
        const rawCutTime: string = (meter as any).lastPowerCutTime ?? '0000-00-00 00:00:00';
        return {
          locationNo: meter.locationNo,
          meterSerialNo: meter.meterSerialNo,
          state: (meter as any).state ?? 'Cut',
          lastPowerCutTime: rawCutTime,
          isCharging,
          durationLabel: isCharging ? this.getChargingDuration(rawCutTime) : '',
          mobile_no: meter._raw?.['mobile_no'] ?? '—',
          _raw: meter._raw
        } as any;
      })
    );
  }
  getMinutesSince(lastPowerCutTime: string): number {
    if (!lastPowerCutTime || lastPowerCutTime.startsWith('0000')) return 0;

    const [datePart, timePart] = lastPowerCutTime.split(' ');
    const [year, month, day] = datePart.split('-').map(Number);
    const [hour, min, sec] = (timePart ?? '00:00:00').split(':').map(Number);

    const since = new Date(year, month - 1, day, hour, min, sec);
    return Math.max(0, Math.floor((Date.now() - since.getTime()) / 60000));
  }
  /**
   * Calculates how long a meter has been in 'Restore' (charging) state.
   * Uses last_power_cut_time as the moment power was restored (charging began).
   * Format: "2023-02-12 14:11:29"
   */
  getChargingDuration(lastPowerCutTime: string): string {
    if (!lastPowerCutTime || lastPowerCutTime.startsWith('0000')) return '—';

    // Parse "YYYY-MM-DD HH:mm:ss"
    const [datePart, timePart] = lastPowerCutTime.split(' ');
    const [year, month, day] = datePart.split('-').map(Number);
    const [hour, min, sec] = (timePart ?? '00:00:00').split(':').map(Number);
    const since = new Date(year, month - 1, day, hour, min, sec);

    const totalMins = Math.max(0, Math.floor((Date.now() - since.getTime()) / 60000));

    const days = Math.floor(totalMins / 1440);
    const hours = Math.floor((totalMins % 1440) / 60);
    const mins = totalMins % 60;

    const parts: string[] = [];
    if (days > 0) parts.push(`${days}d`);
    if (hours > 0) parts.push(`${hours}h`);
    parts.push(`${mins}m`);

    return parts.join(' ');
  }

  // ── Add "state" and "lastPowerCutTime" to MeterReading interface ──────────────
  // Extend MeterReading with these two new fields from the API:
  //
  //   state: 'Restore' | 'Cut';
  //   lastPowerCutTime: string;   // "YYYY-MM-DD HH:mm:ss"  or "0000-00-00 00:00:00"
  //
  // Example mapping from API response list item:
  //   state            → meter.state
  //   last_power_cut_time → meter.lastPowerCutTime

  // ── Sample raw data additions (add to each meter object in getRawSites()) ─────
  //
  //   state: 'Restore',              // or 'Cut'
  //   lastPowerCutTime: '2023-02-12 14:11:29'
}
